import { db, doc, getDoc, collection, query, where, orderBy, limit, startAfter, getDocs, documentId } from '../firebase';
import { Timestamp } from 'firebase/firestore';
import { getCachedQuery, clearCachedKey, primeCachedQuery } from './docCache';
import { SERVICE_TYPE_LABEL, HIRE_TYPES, PLAY_MODES } from '../constants/serviceTypes';
import { keepThaiWords } from './thaiText';

// Cached one-time reads for the public Jobboard (/jobs, /jobs/:id).
//
// WHY: same lesson as marketplaceCache / the 44M-reads incident — public pages never hold a
// listener. Every board read is a bounded getDocs (limit 21 — the rules REJECT a list
// without a limit or above 21) cached in memory + localStorage via docCache. Timestamps are
// flattened to millis BEFORE caching because they do not survive the localStorage round-trip.
// Every key starts with KEY_PREFIX so clearJobsCache() can drop the family (all but config) after
// an accept / approve / cancel without touching other docCache entries.
const KEY_PREFIX = 'jobs:v2:';
const LS_DOCCACHE_PREFIX = 'fl_doccache_v1:'; // docCache's own localStorage prefix
const CONFIG_KEY = 'config';            // suffix; clearJobsCache() keeps this one

const LIST_TTL_MS = 90 * 1000;          // board pages
const DETAIL_TTL_MS = 60 * 1000;        // /jobs/:id
const STATS_TTL_MS = 5 * 60 * 1000;     // header counters
// jobboard_config/main. Long on purpose: only the Console changes it, and the server enforces the
// board mode on every callable (assertBoardOn / loadPosterContext), so a stale copy can only show
// or hide the board for a while — it can never let anyone post or accept.
const CONFIG_TTL_MS = 30 * 60 * 1000;
// job_stats/summary is rewritten by a trigger ~1.5–4 s AFTER a job action, so for this long after
// clearJobsCache() the counters are read but not cached (a cached pre-trigger read would stick for
// STATS_TTL_MS).
const STATS_DIRTY_MS = 10 * 1000;

const QUERY_LIMIT = 21;                 // == the rules' list cap; also our look-ahead row
export const JOBS_PAGE_SIZE = QUERY_LIMIT - 1; // 20 shown, the 21st only says "there is more"

const TAB_STATUSES = {
    open: ['open'],
    active: ['taken', 'inProgress'],
    completed: ['completed'],
};

const issuedKeys = new Set(); // keys created this session — cleared alongside the LS scan
let statsDirtyUntil = 0;      // see STATS_DIRTY_MS

// ─── Local preview (npm start ONLY) ───
// http://localhost:3000/jobs?demo=1 fills the board with SAMPLE jobs and reads nothing from
// Firestore, so the UI can be reviewed before rules / functions are deployed (?demo=0 = off).
// The flag lives in sessionStorage so /jobs → /jobs/:id keeps it. NODE_ENV is 'production'
// in every real build, so this branch is dead code there.
const DEMO_FLAG = 'fl_jobs_demo';
export function isJobsDemo() {
    if (process.env.NODE_ENV !== 'development') return false;
    try {
        const p = new URLSearchParams(window.location.search).get('demo');
        if (p === '1') sessionStorage.setItem(DEMO_FLAG, '1');
        if (p === '0') sessionStorage.removeItem(DEMO_FLAG);
        return sessionStorage.getItem(DEMO_FLAG) === '1';
    } catch (_) {
        return false;
    }
}

let demoCache = null;
function demoJobs() {
    if (demoCache) return demoCache;
    const now = Date.now();
    const min = 60 * 1000, hr = 60 * min, day = 24 * hr;
    const base = (id, f) => ({
        id, schemaVersion: 2, reopened: false, takenByName: null, gameIsCustom: false, customCategory: null,
        ...f, statusAt: f.statusAt ?? f.publishedAt,
    });
    demoCache = [
        base('demoOpenBudgetRov01x', {
            status: 'open', jobType: 'budget', budget: 800, title: 'ดันแรงค์ ROV จาก Diamond V ไป Master',
            description: 'อยากได้คนดันแรงค์ ROV ให้ถึง Master ภายในสัปดาห์นี้ เล่นได้ทุกตำแหน่ง ขอคนเล่นเนียน ไม่ใช้โปร',
            gameId: 'demo-rov', gameName: 'ROV', serviceType: 'rank_boost', hireType: 'fixed', playMode: 'pilot',
            rankFrom: 'Diamond V', rankTo: 'Master', server: 'TH', preferredTime: 'หลัง 2 ทุ่มทุกวัน',
            deliverBy: now + 5 * day, publishedAt: now - 20 * min, expiresAt: now + 14 * day, posterLabel: 'ผู้ว่าจ้าง #K4PZ',
        }),
        base('demoOpenQuoteValo02x', {
            status: 'open', jobType: 'quote', budget: 300, title: 'หาโค้ช Valorant สอนเล่น Jett + การยืนตำแหน่ง',
            description: 'อยากเรียนแบบเล่นด้วยกัน วันละ 1-2 ชม. สอนการใช้ Dash และการวางครอสแฮร์',
            gameId: 'demo-valorant', gameName: 'Valorant', serviceType: 'coaching', hireType: 'hourly', playMode: 'duo',
            rankFrom: 'Gold 2', rankTo: 'Platinum', server: 'AP', preferredTime: 'เสาร์-อาทิตย์',
            deliverBy: null, publishedAt: now - 2 * hr, expiresAt: now + 13 * day, posterLabel: 'ผู้ว่าจ้าง #7HQM',
        }),
        base('demoOpenCustomRo10xx', {
            status: 'open', jobType: 'quote', budget: null, title: 'ช่วยทำเควสต์รายวัน + ดันเจี้ยนกิลด์ทุกวัน 1 สัปดาห์',
            description: 'ไม่ค่อยมีเวลาเล่น อยากให้ช่วยเก็บเควสต์รายวันและลงดันเจี้ยนกิลด์ให้ครบทุกวัน',
            gameId: 'other', gameName: 'Ragnarok Origin', gameIsCustom: true,
            serviceType: 'other', customCategory: 'ทำเควสต์รายวัน', hireType: 'fixed', playMode: null,
            rankFrom: null, rankTo: null, server: 'TH', preferredTime: 'ช่วงกลางวัน',
            deliverBy: now + 8 * day, publishedAt: now - 3 * hr, expiresAt: now + 13 * day, posterLabel: 'ผู้ว่าจ้าง #Q8RN',
        }),
        base('demoOpenQuoteGens03x', {
            status: 'open', jobType: 'quote', budget: null, title: 'ฟาร์ม Primogem + ทำเควสต์ Fontaine ให้จบ',
            description: 'ทำเควสต์หลัก Fontaine ให้จบ และเก็บหีบในแมพให้ได้ 80% ขึ้นไป',
            gameId: 'demo-genshin', gameName: 'Genshin Impact', serviceType: 'farming', hireType: 'fixed', playMode: 'pilot',
            rankFrom: null, rankTo: null, server: 'Asia', preferredTime: null,
            deliverBy: now + 10 * day, publishedAt: now - 5 * hr, expiresAt: now + 13 * day, posterLabel: 'ผู้ว่าจ้าง #R2DX',
        }),
        base('demoOpenBudgetPubg04', {
            status: 'open', jobType: 'budget', budget: 1500, title: 'อัพแรงค์ PUBG Mobile ไป Conqueror',
            description: 'ไอดีอยู่ Ace ดาว 2 อยากให้ถึง Conqueror ก่อนจบซีซัน',
            gameId: 'demo-pubg', gameName: 'PUBG Mobile', serviceType: 'rank_boost', hireType: 'fixed', playMode: 'pilot',
            rankFrom: 'Ace', rankTo: 'Conqueror', server: 'Asia', preferredTime: null,
            deliverBy: now + 7 * day, publishedAt: now - 1 * day, expiresAt: now + 12 * day, posterLabel: 'ผู้ว่าจ้าง #9TWE', reopened: true,
        }),
        base('demoTakenBudgetRov05', {
            status: 'taken', jobType: 'budget', budget: 650, title: 'ลงแรงค์ Placement ROV 10 ตาให้ชนะเยอะสุด',
            gameId: 'demo-rov', gameName: 'ROV', serviceType: 'placement', hireType: 'fixed',
            rankFrom: null, rankTo: null, deliverBy: now + 3 * day,
            publishedAt: now - 9 * hr, takenAt: now - 40 * min, statusAt: now - 40 * min, takenByName: 'ปอนด์', posterLabel: 'ผู้ว่าจ้าง #B8LN',
        }),
        base('demoTakenQuoteAion06', {
            status: 'taken', jobType: 'quote', budget: 2000, title: 'อัพเลเวล AION 2 ตัวละครใหม่ถึงเลเวลแม็กซ์',
            gameId: 'demo-aion2', gameName: 'AION 2', serviceType: 'leveling', hireType: 'fixed',
            rankFrom: 'Lv. 1', rankTo: 'Lv. Max', deliverBy: null,
            publishedAt: now - 1 * day, takenAt: now - 3 * hr, statusAt: now - 3 * hr, takenByName: 'มิว', posterLabel: 'ผู้ว่าจ้าง #F3VA',
        }),
        base('demoWorkingValo07xxx', {
            status: 'inProgress', jobType: 'budget', budget: 1200, title: 'ดันแรงค์ Valorant Silver → Gold 3',
            gameId: 'demo-valorant', gameName: 'Valorant', serviceType: 'rank_boost', hireType: 'fixed',
            rankFrom: 'Silver 1', rankTo: 'Gold 3', deliverBy: now + 4 * day,
            publishedAt: now - 2 * day, takenAt: now - 2 * day + 2 * hr, startedAt: now - 1 * day, statusAt: now - 1 * day,
            takenByName: 'ปอนด์', posterLabel: 'ผู้ว่าจ้าง #M6KC',
        }),
        base('demoDoneGenshin08xxx', {
            status: 'completed', jobType: 'quote', budget: 500, title: 'เคลียร์ Spiral Abyss ชั้น 12 ให้ได้ 36 ดาว',
            gameId: 'demo-genshin', gameName: 'Genshin Impact', serviceType: 'other', customCategory: 'เคลียร์ด่าน', hireType: 'fixed',
            rankFrom: null, rankTo: null, deliverBy: null,
            publishedAt: now - 6 * day, takenAt: now - 6 * day + 1 * hr, startedAt: now - 5 * day, completedAt: now - 1 * day,
            statusAt: now - 1 * day, takenByName: 'โอ๊ต', posterLabel: 'ผู้ว่าจ้าง #Z5PJ',
        }),
        base('demoDoneRov09xxxxxxx', {
            status: 'completed', jobType: 'budget', budget: 450, title: 'สอนเล่นโรมมิ่ง ROV 3 ชั่วโมง',
            gameId: 'demo-rov', gameName: 'ROV', serviceType: 'coaching', hireType: 'fixed',
            rankFrom: null, rankTo: null, deliverBy: null,
            publishedAt: now - 9 * day, takenAt: now - 9 * day + 30 * min, startedAt: now - 8 * day, completedAt: now - 3 * day,
            statusAt: now - 3 * day, takenByName: 'มิว', posterLabel: 'ผู้ว่าจ้าง #H7GX',
        }),
    ];
    return demoCache;
}

function cacheKey(suffix) {
    const key = KEY_PREFIX + suffix;
    issuedKeys.add(key);
    return key;
}

// fetchJob's key — also used by fetchJobsPage to prime it, so both always name the same entry.
const jobDocKey = (jobId) => cacheKey(`doc:${jobId}`);

// Timestamp | {seconds,nanoseconds} (a Timestamp after JSON) | Date | number → millis (or null).
export function toMillis(v) {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v instanceof Date) return v.getTime();
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    return null;
}

// Recursively replace Firestore Timestamps with millis so the value is JSON-safe.
function flatten(value) {
    if (value === null || value === undefined) return value;
    if (Array.isArray(value)) return value.map(flatten);
    if (typeof value === 'object') {
        if (typeof value.toMillis === 'function') return value.toMillis();
        const out = {};
        for (const [k, v] of Object.entries(value)) out[k] = flatten(v);
        return out;
    }
    return value;
}

function encodeCursor(snap) {
    const ms = toMillis(snap.get('statusAt'));
    if (ms === null) return null;
    return `${ms}_${snap.id}`;
}

// "1712345678901_AbCd…" → [Timestamp, docId]. Never compare raw millis against a Timestamp field.
function decodeCursor(cursor) {
    if (typeof cursor !== 'string') return null;
    const i = cursor.indexOf('_');
    if (i <= 0) return null;
    const ms = Number(cursor.slice(0, i));
    const id = cursor.slice(i + 1);
    if (!Number.isFinite(ms) || !/^[A-Za-z0-9]{1,40}$/.test(id)) return null;
    return [Timestamp.fromMillis(ms), id];
}

// One board page. Any subset of gameId / serviceType / jobType is applied together (equality
// filters, AND). Every combination has its own (status, …filters, statusAt desc) composite index
// in firestore.indexes.json — add one there before adding a new filter field here.
// → { items: [{ id, ...fields (timestamps as millis) }], nextCursor: string|null }
const FILTER_FIELDS = ['gameId', 'serviceType', 'jobType']; // fixed order == index field order

export async function fetchJobsPage({ tab = 'open', gameId = '', serviceType = '', jobType = '', cursor = null } = {}) {
    const statuses = TAB_STATUSES[tab] || TAB_STATUSES.open;
    const values = { gameId, serviceType, jobType };
    const filters = FILTER_FIELDS.filter(f => values[f]).map(f => [f, values[f]]);

    if (isJobsDemo()) {
        const items = demoJobs()
            .filter(j => statuses.includes(j.status) && filters.every(([f, v]) => j[f] === v))
            .sort((a, b) => b.statusAt - a.statusAt);
        return { items, nextCursor: null };
    }

    // Key names every filter slot ('*' = unset) so each combination caches separately (the
    // named form also never collides with entries cached while only one filter was applied).
    const key = cacheKey(`list:${tab}:g=${gameId || '*'}:s=${serviceType || '*'}:j=${jobType || '*'}:${cursor || 0}`);
    return getCachedQuery(key, LIST_TTL_MS, async () => {
        if (!db) return { items: [], nextCursor: null };
        const parts = [
            statuses.length === 1 ? where('status', '==', statuses[0]) : where('status', 'in', statuses),
        ];
        filters.forEach(([f, v]) => parts.push(where(f, '==', v)));
        parts.push(orderBy('statusAt', 'desc'), orderBy(documentId(), 'desc'));
        const after = decodeCursor(cursor);
        if (after) parts.push(startAfter(after[0], after[1]));
        parts.push(limit(QUERY_LIMIT));

        const readAt = Date.now();
        const snap = await getDocs(query(collection(db, 'public/data/jobs'), ...parts));
        const docs = snap.docs.slice(0, JOBS_PAGE_SIZE);
        const items = docs.map(d => ({ id: d.id, ...flatten(d.data()) }));
        // Each row is the whole public doc in exactly fetchJob's shape, so prime /jobs/:id with it:
        // opening a card then skips a getDoc behind a full-page spinner. Memory only and stamped now,
        // so fetchJob still re-reads DETAIL_TTL_MS after this read, as before.
        items.forEach(it => primeCachedQuery(jobDocKey(it.id), it, readAt));
        const nextCursor = snap.docs.length > JOBS_PAGE_SIZE ? encodeCursor(docs[docs.length - 1]) : null;
        return { items, nextCursor };
    });
}

// One public job (or null when it was closed / hidden / never existed — null is cached too).
export async function fetchJob(jobId) {
    if (!jobId || !/^[A-Za-z0-9]{1,40}$/.test(jobId)) return null;
    if (isJobsDemo()) return demoJobs().find(j => j.id === jobId) || null;
    return getCachedQuery(jobDocKey(jobId), DETAIL_TTL_MS, async () => {
        if (!db) return null;
        const snap = await getDoc(doc(db, 'public/data/jobs', jobId));
        return snap.exists() ? { id: snap.id, ...flatten(snap.data()) } : null;
    });
}

// Header counters, written only by Cloud Functions (recomputeJobStats).
export async function fetchJobStats() {
    const empty = { open: 0, taken: 0, inProgress: 0, completed: 0 };
    if (isJobsDemo()) {
        return demoJobs().reduce((s, j) => (j.status in s ? { ...s, [j.status]: s[j.status] + 1 } : s), empty);
    }
    const read = async () => {
        if (!db) return empty;
        const snap = await getDoc(doc(db, 'public/data/job_stats/summary'));
        const d = snap.exists() ? snap.data() : {};
        const n = (v) => (Number.isFinite(v) && v > 0 ? Math.trunc(v) : 0);
        return { open: n(d.open), taken: n(d.taken), inProgress: n(d.inProgress), completed: n(d.completed) };
    };
    try {
        if (Date.now() < statsDirtyUntil) return await read(); // dirty window: read, don't cache
        const key = cacheKey('stats');
        const stats = await getCachedQuery(key, STATS_TTL_MS, read);
        // A read still in flight when clearJobsCache() ran got cached after it — drop that too.
        if (Date.now() < statsDirtyUntil) clearCachedKey(key);
        return stats;
    } catch (_) {
        return empty;
    }
}

// Feature switch. A missing doc (or a failed read) means 'off'.
export async function fetchJobboardConfig() {
    if (isJobsDemo()) return { enabled: 'public' };
    try {
        return await getCachedQuery(cacheKey(CONFIG_KEY), CONFIG_TTL_MS, async () => {
            if (!db) return { enabled: 'off' };
            const snap = await getDoc(doc(db, 'jobboard_config/main'));
            const e = snap.exists() ? snap.data().enabled : 'off';
            return { enabled: ['off', 'staff', 'public'].includes(e) ? e : 'off' };
        });
    } catch (_) {
        return { enabled: 'off' };
    }
}

// Drop every jobs:v2:* entry (memory + localStorage) except the board config — call after an
// action that changes public/data/jobs (approve / accept / cancel / hide …) so the actor sees the
// change immediately instead of after the TTL. Config stays: no job action changes it. Also opens
// the stats dirty window (STATS_DIRTY_MS).
export function clearJobsCache() {
    const keys = new Set(issuedKeys);
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith(LS_DOCCACHE_PREFIX + KEY_PREFIX)) keys.add(k.slice(LS_DOCCACHE_PREFIX.length));
        }
    } catch (_) { }
    keys.delete(KEY_PREFIX + CONFIG_KEY); // after the scan, which re-adds it from localStorage
    keys.forEach(k => clearCachedKey(k));
    issuedKeys.clear();
    statsDirtyUntil = Date.now() + STATS_DIRTY_MS;
}

// ─── Display helpers shared by JobsPage / JobDetailPage / MyJobsPage / modals ───
// jobCategoryLabel / playModeLabel / adminLabel (and so jobStatusChip) return keepThaiWords-glued
// text: every caller only renders it — never compare it, store it or send it anywhere.

const HIRE_LABEL = Object.fromEntries(HIRE_TYPES.map(h => [h.value, h.label]));
const PLAY_LABEL = Object.fromEntries(PLAY_MODES.map(p => [p.value, p.label]));

export const formatBaht = (n) => '฿' + new Intl.NumberFormat('th-TH').format(Number(n) || 0);
export const serviceLabel = (v) => SERVICE_TYPE_LABEL[v] || 'งานทั่วไป';
// Category as the poster wrote it: serviceType 'other' carries its own customCategory text.
export function jobCategoryLabel(job) {
    const label = job?.serviceType === 'other' && job?.customCategory ? job.customCategory : serviceLabel(job?.serviceType);
    return keepThaiWords(label);
}
export const hireLabel = (v) => HIRE_LABEL[v] || '-';
export const playModeLabel = (v) => keepThaiWords(PLAY_LABEL[v] || '');

// Public admin label: a nickname from staff_profiles ("K7Q2" / "#K7Q2"), or the team fallback.
export const adminLabel = (name) => keepThaiWords(name ? `แอดมิน ${name}` : 'ทีมงาน Fastlane');
export const adminInitial = (name) => (String(name || 'F').replace(/^#/, '').charAt(0) || 'F').toUpperCase();

export function timeAgoTh(v) {
    const ms = toMillis(v);
    if (!ms) return '-';
    const m = Math.floor((Date.now() - ms) / 60000);
    if (m < 1) return 'เมื่อครู่';
    if (m < 60) return `${m} นาทีที่แล้ว`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} ชม.ที่แล้ว`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} วันที่แล้ว`;
    return formatDateTh(ms);
}

export function formatDateTh(v, withTime = false) {
    const ms = toMillis(v);
    if (!ms) return '-';
    const opts = { day: 'numeric', month: 'short', year: '2-digit' };
    if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; }
    return new Date(ms).toLocaleString('th-TH', opts);
}

// "ขอใบเสนอราคา" or "จ้างตามงบ ฿X" (+ Tailwind classes for the light board).
export function jobTypeBadge(job) {
    if (job?.jobType === 'budget') {
        return { text: `จ้างตามงบ ${formatBaht(job.budget)}`, cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    return { text: 'ขอใบเสนอราคา', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
}

// Status chip for a PUBLIC job doc (plan 6.1).
export function jobStatusChip(job) {
    const who = adminLabel(job?.takenByName);
    switch (job?.status) {
        case 'open':
            return { text: '🟢 เปิดรับ', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
        case 'taken':
            return job.jobType === 'budget'
                ? { text: `🤝 ${who} รับตามงบ ${formatBaht(job.budget)} · รอชำระ`, cls: 'bg-pink-50 text-pink-700 border-pink-200' }
                : { text: `🤝 ${who} รับงานแล้ว · กำลังคุยรายละเอียด`, cls: 'bg-pink-50 text-pink-700 border-pink-200' };
        case 'idle':
            return { text: '⏸️ รอดำเนินการ', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
        case 'inProgress':
            return { text: `🔧 กำลังดำเนินการ · ${who}`, cls: 'bg-orange-50 text-orange-700 border-orange-200' };
        case 'completed':
            return { text: `✅ เสร็จแล้ว · โดย${who}`, cls: 'bg-green-50 text-green-700 border-green-200' };
        default:
            return { text: job?.status || '-', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
    }
}
